import { useState, useEffect } from "react";
import { X, Upload, Loader2, Save, Trash2, GripVertical, Star } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { CURRENCIES, CURRENCY_LABEL } from "@/lib/contactChannels";
import { toast } from "sonner";

export default function ListingEditModal({ listing, isAdmin = false, onClose, onSaved }) {
  const [title, setTitle] = useState(listing.name || listing.title || "");
  const [description, setDescription] = useState(listing.description || "");
  const [mainParams, setMainParams] = useState(listing.main_parameters || "");
  const [condition, setCondition] = useState(listing.condition || "second-hand");
  const [currency, setCurrency] = useState(listing.currency || "RON");
  const hasRange = listing.price_min != null && listing.price_max != null;
  const [priceType, setPriceType] = useState(hasRange ? "range" : "fixed");
  const [priceOnRequest, setPriceOnRequest] = useState(!!listing.price_on_request);
  const [price, setPrice] = useState(listing.price ? String(listing.price) : "");
  const [priceMin, setPriceMin] = useState(listing.price_min != null ? String(listing.price_min) : "");
  const [priceMax, setPriceMax] = useState(listing.price_max != null ? String(listing.price_max) : "");
  const [images, setImages] = useState(listing.images || []);
  const [mkPrimary, setMkPrimary] = useState(listing.mk_primary || "");
  const [mkSub, setMkSub] = useState(listing.mk_subcategory || "");
  const [tree, setTree] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    api.get("/marketplace-taxonomy").then((r) => setTree(r.data.tree || [])).catch(() => {});
  }, []);

  const subs = (tree.find((g) => g.id === mkPrimary) || {}).subcategories || [];

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/listings/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      setImages((prev) => [...prev, ...uploaded]);
    } catch { toast.error("Eroare la încărcarea imaginii."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeImage = (url) => setImages((prev) => prev.filter((i) => i !== url));

  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    setImages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(null);
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Titlul este obligatoriu."); return; }
    setSaving(true);
    try {
      const isRange = priceType === "range";
      const payload = {
        title: title.trim(), description, main_parameters: mainParams, condition,
        currency, price_on_request: priceOnRequest,
        price: priceOnRequest || isRange ? null : (price ? Number(price) : null),
        price_min: !priceOnRequest && isRange && priceMin !== "" ? Number(priceMin) : null,
        price_max: !priceOnRequest && isRange && priceMax !== "" ? Number(priceMax) : null,
        images,
        mk_primary: mkPrimary, mk_subcategory: mkSub,
      };
      const url = isAdmin ? `/admin/listings/${listing.id}` : `/listings/${listing.id}`;
      await api.put(url, payload);
      toast.success("Anunț actualizat cu succes.");
      onSaved && onSaved();
      onClose();
    } catch { toast.error("Eroare la salvare. Încearcă din nou."); }
    finally { setSaving(false); }
  };

  const inputCls = "mt-1.5 w-full border border-slate-300 px-3 py-2 text-sm focus:border-orange-500 outline-none";
  const lbl = "text-[11px] uppercase tracking-wide font-bold text-slate-500";

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8" data-testid="listing-edit-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl border border-slate-200 shadow-2xl my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="font-black text-lg tracking-tight">Editează anunțul</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="edit-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className={lbl}>Titlu</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="edit-title" />
          </div>

          {/* Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Categorie</label>
              <select value={mkPrimary} onChange={(e) => { setMkPrimary(e.target.value); setMkSub(""); }} className={`${inputCls} bg-white`} data-testid="edit-mk-primary">
                <option value="">—</option>
                {tree.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Subcategorie</label>
              <select value={mkSub} onChange={(e) => setMkSub(e.target.value)} disabled={!subs.length} className={`${inputCls} bg-white disabled:bg-slate-100`} data-testid="edit-mk-sub">
                <option value="">—</option>
                {subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={lbl}>Descriere</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} className={`${inputCls} resize-y`} data-testid="edit-description" />
          </div>

          <div>
            <label className={lbl}>Parametri principali</label>
            <textarea value={mainParams} onChange={(e) => setMainParams(e.target.value)} rows={2} className={`${inputCls} resize-y`} data-testid="edit-main-params" />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Monedă</label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={`${inputCls} bg-white`} data-testid="edit-currency">
                {CURRENCIES.map((c) => <option key={c} value={c}>{CURRENCY_LABEL[c]}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Stare</label>
              <select value={condition} onChange={(e) => setCondition(e.target.value)} className={`${inputCls} bg-white`} data-testid="edit-condition">
                <option value="nou">Nou</option>
                <option value="second-hand">Second-hand</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="ept" checked={priceType === "fixed"} onChange={() => setPriceType("fixed")} data-testid="edit-ptype-fixed" /> Preț fix
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="ept" checked={priceType === "range"} onChange={() => setPriceType("range")} data-testid="edit-ptype-range" /> Interval de preț
            </label>
          </div>

          {priceType === "fixed" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Preț ({currency})</label>
                <input type="number" value={price} disabled={priceOnRequest} onChange={(e) => setPrice(e.target.value)} className={`${inputCls} disabled:bg-slate-100 disabled:text-slate-400`} data-testid="edit-price" />
                <label className="flex items-center gap-2 mt-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={priceOnRequest} onChange={(e) => setPriceOnRequest(e.target.checked)} data-testid="edit-price-on-request" /> Preț la cerere
                </label>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lbl}>De la ({currency})</label>
                <input type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} className={inputCls} data-testid="edit-price-min" />
              </div>
              <div>
                <label className={lbl}>Până la ({currency})</label>
                <input type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} className={inputCls} data-testid="edit-price-max" />
              </div>
            </div>
          )}

          {/* Images with drag reorder */}
          <div>
            <label className={lbl}>Imagini <span className="normal-case font-normal text-slate-400">(trage pentru a reordona · prima = copertă)</span></label>
            <label className="mt-1.5 flex flex-col items-center justify-center gap-2 w-full min-h-[6rem] border-2 border-dashed border-slate-300 cursor-pointer hover:border-orange-500 hover:bg-orange-50/40 transition-colors text-slate-400 hover:text-orange-600 p-4" data-testid="edit-upload-label">
              {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
              <span className="text-xs font-semibold">{uploading ? "Se încarcă..." : "Adaugă imagini"}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={uploading} data-testid="edit-upload-input" />
            </label>
            {images.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="edit-image-grid">
                {images.map((img, idx) => (
                  <div
                    key={img}
                    draggable
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(idx)}
                    className={`relative w-24 h-24 border ${idx === 0 ? "border-orange-500 ring-2 ring-orange-200" : "border-slate-200"} cursor-move`}
                    data-testid={`edit-image-${idx}`}
                  >
                    <img src={mediaUrl(img)} alt="preview" className="w-full h-full object-cover" />
                    <GripVertical className="absolute top-0.5 left-0.5 w-4 h-4 text-white drop-shadow" />
                    {idx === 0 && <span className="absolute bottom-0 left-0 right-0 bg-orange-600 text-white text-[9px] font-bold uppercase text-center py-0.5 flex items-center justify-center gap-0.5"><Star className="w-2.5 h-2.5" /> Copertă</span>}
                    <button onClick={() => removeImage(img)} className="absolute -top-2 -right-2 bg-slate-900 text-white rounded-full p-1" data-testid="edit-remove-image"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold uppercase tracking-wide border border-slate-300 hover:border-slate-900 transition-colors" data-testid="edit-cancel">Anulează</button>
          <button onClick={save} disabled={saving || uploading} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50" data-testid="edit-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvează
          </button>
        </div>
      </div>
    </div>
  );
}
